// Explorer > one talent: who they are, how they perform, and every project we
// approached them for.
//
// TWO SOURCES, deliberately not blended (admin/CLAUDE.md, ARCHITECTURE.md's
// metric boundary):
//
//   statistics — BI. `GET /api/admin/talent/metrics`, the curated warehouse
//                snapshot the Talent dashboard reads, filtered to this talent.
//                Every rate and average on this page comes from there and
//                nowhere else. When the snapshot is missing, stale, or the
//                viewer lacks `talent_management`, the section SAYS SO — it
//                never falls back to counting the operational rows below, which
//                would be a second definition of the same numbers.
//   projects   — operational. `GET /api/admin/talents/{sellerId}`, live product
//                truth, per-record facts only: when we approached, how fast they
//                replied, what they offered, where the thread ended up.
//
// The two are also gated differently, and that is not a bug to smooth over: an
// `explorer` holder sees the projects and is told the statistics need the Talent
// Management grant.
import {
  Badge,
  Card,
  SortableTh,
  TableExport,
  TableFilters,
  type FilterColumn,
  useColumnFilters,
  usePagedRows,
  useTableSort,
} from "@recruiter/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { AreaBoundary } from "../components/area-gate";
import { BackLink } from "../components/back-link";
import { SellerThreadDetail } from "../components/seller-thread-detail";
import { EmptyState, LoadingState, TableSkeleton, errorMessage } from "../components/states";
import { ApiError } from "../lib/api";
import { formatDateTime, formatMinutes, formatOfferPrice } from "../lib/format";
import { isDerivedTitle, projectTitle } from "../lib/project-title";
import { explorerTalentQuery } from "../lib/queries";
import {
  TALENT_PROJECT_SORT_ACCESSORS,
  parseTalentThreads,
  threadStatus,
  type TalentProjectSortKey,
  type TalentThreadRow,
} from "../lib/seller-threads";
import { ShowMoreRow } from "../components/show-more-row";
import {
  EMPTY_FILTERS,
  selectionFor,
  selectionForSeller,
  sellerRates,
  stripFigures,
  type Ratio,
} from "../lib/talent";
import { useTalentResult } from "../lib/use-talent-result";
import {
  resolveSelectedThreadId,
  seedThreadId,
  type ThreadSelection,
} from "../lib/thread-selection";
import "./explorer.css";

/** Statistics are read over ALL TIME, not a preset window. The dashboard's
 *  ranges exist for comparing talents against each other in a period; on one
 *  talent's own page the question is their whole record with us. */

const COLUMNS: { key: TalentProjectSortKey; label: string; numeric?: boolean }[] = [
  { key: "project", label: "Project" },
  { key: "client", label: "Client" },
  { key: "approachedAt", label: "Approached" },
  { key: "repliedInMinutes", label: "Replied in", numeric: true },
  { key: "offerInMinutes", label: "To offer", numeric: true },
  { key: "messageCount", label: "Msgs", numeric: true },
  { key: "offerPrice", label: "Offer", numeric: true },
  { key: "status", label: "Status" },
];

/** Status is the only repeating column here: everything else is one project, one
 *  client, one timestamp or one figure. It is also the question the page is
 *  opened with once a talent has been approached a dozen times, which is "where
 *  did the ones that went somewhere go". Hides itself while every thread is in
 *  the same state, so a talent with two projects sees no strip. */
const TALENT_PROJECT_FILTERS: readonly FilterColumn<TalentThreadRow>[] = [
  {
    key: "status",
    label: "Filter by status",
    allLabel: "Any status",
    // On the printed LABEL, matching the sort accessor and the badge, so a state
    // this console has no entry for still filters by the words it shows.
    value: (r) => threadStatus(r.state, r.latestOffer).label,
  },
];

/** One statistic about this talent. `benchmark` is the MIDDLE TALENT's figure for
 *  the same metric, printed beside theirs.
 *
 *  It is deliberately the population's median and NOT this talent's own: the
 *  warehouse ships additive sums and counts per seller, never raw per-thread
 *  minutes, so "the middle of this person's threads" is not computable here at
 *  all. The middle of the POPULATION is, and on a single-talent page it answers
 *  the question the reader actually has — "is 2.1 hours good?" — which their own
 *  median would not. The label says "median talent" everywhere so the two can
 *  never be confused. */
function StatCard({
  label,
  value,
  benchmark,
  hint,
}: {
  label: string;
  value: string;
  benchmark?: string;
  hint?: string;
}) {
  return (
    <Card padding="md" className="ex-stat">
      <span className="ex-stat__label">{label}</span>
      <span className="ex-stat__figures">
        <strong className="ex-stat__value">{value}</strong>
        {benchmark === undefined ? null : (
          <span className="ex-stat__benchmark">
            <span className="ex-stat__benchmark-key">median talent</span>
            {benchmark}
          </span>
        )}
      </span>
      {hint ? <span className="ex-stat__hint">{hint}</span> : null}
    </Card>
  );
}

function pct(ratio: Ratio): string {
  return ratio.value === null ? "-" : `${Math.round(ratio.value * 100)}%`;
}

/** The BI half. Deliberately a separate component with its own query, its own
 *  failure states and its own provenance line, so it cannot silently borrow a
 *  number from the operational half. */
/** All time, both sides unbounded: the explorer asks what a talent has done
 *  overall, not in a window. */
const ALL_TIME = { from: null, to: null };

function Statistics({ sellerId }: { sellerId: string }) {
  // TWO selections, because they are two different questions and the warehouse
  // answers each one on its own: this talent's lifetime row, and the population it
  // sits in. They used to be one pass over a snapshot the page already held; now
  // the page holds nothing, so it asks for exactly the two numbers it renders.
  //
  // The benchmark asks for `limit: 1` on purpose. It needs the TOTALS over every
  // seller, not the sellers — and the totals cover the whole selection regardless
  // of the page, so one row is the cheapest way to carry them.
  const mine = useTalentResult(selectionForSeller(sellerId));
  const population = useTalentResult(
    useMemo(() => selectionFor(ALL_TIME, EMPTY_FILTERS, "approached", "desc", 1, 0), []),
  );

  const row = mine.result?.rows[0] ?? null;
  const aggregate = row ? { ...row, ...sellerRates(row) } : null;
  const benchmark = population.result ? stripFigures(population.result.totals) : null;
  const result = mine.result;

  // BOTH, not just `mine`. They are independent selections with their own queries
  // and their own Pusher waits, and `mine` (a limit-1 lookup) usually answers first.
  // Checking only `mine` rendered "the warehouse has no rows for this talent yet"
  // — for a talent that demonstrably has rows — for as long as the population query
  // took, and permanently if it never landed.
  if (mine.isLoading || mine.isPending || population.isLoading || population.isPending) {
    return (
      <div className="ex-stats" role="status" aria-busy="true">
        <LoadingState label="Loading statistics…" />
      </div>
    );
  }

  // The statistics live behind `talent_management`, the page behind `explorer`.
  // A viewer holding only the latter gets the projects and an honest note here,
  // never a broken page and never a substituted number.
  // Either query failing is a statistics failure, not a statement about this
  // talent. Checking only `mine` sent a failed POPULATION query to the "no rows for
  // this talent yet" branch below, which is a claim about them rather than about us.
  if (mine.isError || population.isError) {
    const failure = mine.isError ? mine.errorObject : population.errorObject;
    const forbidden = failure instanceof ApiError && failure.status === 403;
    return (
      <p className="ex-muted ex-stats__note">
        {forbidden
          ? "Statistics come from the analytics warehouse and need the Talent Management area. The projects below are unaffected."
          : `Statistics are unavailable right now. ${errorMessage(failure)}`}
      </p>
    );
  }

  if (result === null) {
    return (
      <p className="ex-muted ex-stats__note">
        {mine.error ??
          "No warehouse answer yet. These figures come from the analytics warehouse, so they appear once it responds."}
      </p>
    );
  }
  // The benchmark missing is OUR problem, not a fact about this talent — say so
  // separately rather than folding it into the "no rows" message below.
  if (benchmark === null) {
    return (
      <p className="ex-muted ex-stats__note">
        {population.error ??
          "The population figures these statistics compare against are unavailable right now."}
      </p>
    );
  }
  if (aggregate === null) {
    return (
      <p className="ex-muted ex-stats__note">
        The warehouse has no rows for this talent yet. Outreach reaches the analytics projection on
        the next refresh, so a talent approached today has no statistics until then. Their projects
        below are live either way.
      </p>
    );
  }

  const snapshot = result;
  const source = snapshot.sourceUpdatedAt ?? snapshot.generatedAt;
  return (
    <>
      {mine.refreshing ? (
        <div className="ex-stale" role="alert">
          <strong>Checking for newer data.</strong> These are the last numbers the warehouse gave
          us; a fresher answer is on its way and will replace them here.
        </div>
      ) : null}

      <section className="ex-stats" aria-label="Talent statistics">
        <StatCard
          label="Approached"
          value={aggregate.approached.toLocaleString()}
          // horizon-allow: seller_threads has no retention clock
          hint="Times we reached out, all time"
        />
        <StatCard
          label="Response rate"
          value={pct(aggregate.responseRate)}
          hint={
            snapshot.replyIncludesAutoRepliesCaveat
              ? "Includes automatic replies and PA messages"
              : `${aggregate.responseRate.numerator} of ${aggregate.responseRate.denominator} replied`
          }
        />
        <StatCard
          label="Avg response"
          value={formatMinutes(aggregate.avgResponseMinutes)}
          benchmark={formatMinutes(benchmark.medianResponseMinutes)}
          hint="From our first message"
        />
        <StatCard
          label="Avg to offer"
          value={formatMinutes(aggregate.avgTimeToOfferMinutes)}
          benchmark={formatMinutes(benchmark.medianTimeToOfferMinutes)}
          hint="First message to offer created"
        />
        <StatCard
          label="Willingness"
          value={pct(aggregate.willingness)}
          hint="Warehouse definition, not computed here"
        />
        <StatCard
          label="Chosen"
          value={aggregate.chosen.toLocaleString()}
          hint={`${pct(aggregate.chosenRate)} of offers`}
        />
      </section>

      <div className="ex-provenance">
        <span>
          Warehouse data as of{" "}
          <strong>{source ? source.replace("T", " ").slice(0, 16) : "unknown"}</strong>
        </span>
        <span>
          Offers <strong>{aggregate.offers.toLocaleString()}</strong>, declined{" "}
          <strong>{aggregate.declined.toLocaleString()}</strong>, questions asked{" "}
          <strong>{aggregate.questions.toLocaleString()}</strong>
        </span>
        {snapshot.searchTierSource !== "observed" ? (
          <Badge tone="neutral" size="sm">
            Search Tier not yet recorded for this period
          </Badge>
        ) : null}
      </div>
    </>
  );
}

/** One project row's talent-side outcome. */
function ProjectRow({
  row,
  open,
  onToggle,
  detailId,
}: {
  row: TalentThreadRow;
  open: boolean;
  onToggle: () => void;
  detailId: string;
}) {
  const titleable = {
    id: row.projectId ?? row.id,
    name: row.projectName,
    summary: row.projectSummary,
  };
  const status = threadStatus(row.state, row.latestOffer);
  return (
    <tr data-active={open ? "true" : undefined} className="ex-seller-row" onClick={onToggle}>
      <td>
        <div className="ex-seller-cell">
          {/* The name walks to the PROJECT (stopPropagation: the row click
              toggles the thread, the name click must only navigate). A legacy
              row with no project id keeps the plain title. */}
          {row.projectId ? (
            <Link
              to="/explorer/projects/$projectId"
              params={{ projectId: row.projectId }}
              className="ex-primary-link"
              onClick={(event) => event.stopPropagation()}
            >
              <span className="rc-cell-strong">{projectTitle(titleable)}</span>
            </Link>
          ) : (
            <span className="rc-cell-strong">{projectTitle(titleable)}</span>
          )}
          {isDerivedTitle(titleable) ? <span className="ex-muted">Not named yet</span> : null}
          {/* The keyboard's way to the toggle, now that the title is a link.
              Same text-styled control as before; the whole row still toggles
              on click. */}
          <button
            type="button"
            className="ex-seller-open"
            aria-expanded={open}
            aria-controls={open ? detailId : undefined}
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          >
            <span className="ex-muted">{open ? "Hide thread" : "View thread"}</span>
          </button>
          {row.recommended ? (
            <Badge tone="success" size="sm">
              Recommended
            </Badge>
          ) : null}
        </div>
      </td>
      <td>{row.ownerDisplayName || row.ownerEmail || "Unknown client"}</td>
      <td>{formatDateTime(row.approachedAt)}</td>
      <td className="rc-num">{formatMinutes(row.repliedInMinutes)}</td>
      <td className="rc-num">{formatMinutes(row.offerInMinutes)}</td>
      <td className="rc-num">{row.messageCount.toLocaleString()}</td>
      <td className="rc-num">
        {formatOfferPrice(
          row.latestOffer?.priceCents ?? null,
          row.latestOffer?.currency ?? null,
          row.latestOffer?.priceMaxCents ?? null,
        )}
      </td>
      <td>
        <Badge tone={status.tone} size="sm">
          {status.label}
        </Badge>
      </td>
      {/* This talent's conversation with THAT project's client, in Fiverr OS
          Chats Research. The mirror of the Sellers tab's column, read from the
          other end: there one client across many talents, here one talent across
          many clients. It belongs on the row rather than in the thread panel,
          because the panel shows the STERLING thread (the per-project bot), which
          is a different conversation entirely.

          A plain hyphen, not a disabled control, when either side has no numeric
          Fiverr id: there is nothing to open, and a dead button invites the click
          anyway. stopPropagation so following the link does not also toggle the
          row's thread open behind it. */}
      <td>
        {row.fiverrOsChatUrl ? (
          <a
            className="ex-os-chat-link"
            href={row.fiverrOsChatUrl}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(event) => event.stopPropagation()}
            title="Open this client and talent's Fiverr conversation in Fiverr OS"
          >
            Fiverr OS
          </a>
        ) : (
          <span className="ex-muted">-</span>
        )}
      </td>
    </tr>
  );
}

const DETAIL_ID = "talent-thread-detail";

function TalentView({
  sellerId,
  threads,
  projectId,
}: {
  sellerId: string;
  threads: TalentThreadRow[];
  projectId?: string;
}) {
  // `?project=` preselects the engagement a link arrived pointing at, so coming
  // from a search or a project opens THAT thread rather than an unexpanded list.
  // Once the reader clicks around, their choice wins — a URL that kept
  // re-asserting itself would fight them on every toggle — but only for as long
  // as the URL still says what it said when they chose: hopping ?project=A →
  // ?project=B re-renders this same instance, so the selection is DERIVED, with
  // each manual choice stamped by the param it was made under (thread-selection.ts
  // has the full story; Monday 3187516446).
  const [selection, setSelection] = useState<ThreadSelection>(() => ({
    from: projectId,
    id: seedThreadId(threads, projectId),
  }));
  const selectedId = resolveSelectedThreadId(selection, threads, projectId);
  const filters = useColumnFilters(threads, TALENT_PROJECT_FILTERS, {
    restore: "explorer-talent/projects/filters",
  });
  const { rows, toggle, ariaSort } = useTableSort(
    filters.rows,
    TALENT_PROJECT_SORT_ACCESSORS,
    null,
    { restore: "explorer-talent/projects/sort" },
  );
  const paged = usePagedRows(rows, { restore: "explorer-talent/projects/window" });
  const selected = threads.find((t) => t.id === selectedId) ?? null;

  // Identity comes from the freshest thread that captured a profile, which is
  // what the backend already ordered for us.
  const head = threads[0];
  const displayName = threads.find((t) => t.displayName !== t.sellerId)?.displayName ?? sellerId;
  const avatarUrl = threads.find((t) => t.avatarUrl !== null)?.avatarUrl ?? null;
  const headline = threads.find((t) => t.headline !== null)?.headline ?? null;

  return (
    <div className="ad-page">
      <header className="ex-detail__head">
        <BackLink to="/explorer" label="Back to Explorer" />
        <div className="ex-detail__title-row">
          <div className="ex-talent__id">
            {avatarUrl ? <img className="ex-talent-avatar" src={avatarUrl} alt="" /> : null}
            <div>
              <h1 className="ad-page__title">{displayName}</h1>
              <p className="ad-page__sub">
                {head.profileUrl ? (
                  <a
                    className="ex-owner-link"
                    href={head.profileUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    @{sellerId}
                  </a>
                ) : (
                  `@${sellerId}`
                )}
                {headline ? `, ${headline}` : ""}
              </p>
            </div>
          </div>
          <div className="ex-badges">
            {head.searchTier ? <Badge tone="neutral">Tier {head.searchTier}</Badge> : null}
            {head.sellerLevel ? <Badge tone="neutral">{head.sellerLevel}</Badge> : null}
            {head.country ? <Badge tone="neutral">{head.country}</Badge> : null}
            {head.isPro ? <Badge tone="accent">Pro</Badge> : null}
          </div>
        </div>
      </header>

      <Statistics sellerId={sellerId} />

      <section className="ad-section">
        <div className="ex-section-head">
          <h2 className="ad-section__title">Projects approached</h2>
          <span className="ex-count">
            {threads.length.toLocaleString()} {threads.length === 1 ? "project" : "projects"}, live
            records
          </span>
        </div>

        <TableFilters controls={filters.controls} active={filters.active} onClear={filters.clear} />
        {filters.rows.length === 0 ? (
          <p className="ex-muted">No project matches those filters.</p>
        ) : null}

        <div className="rc-table-wrap">
          <table className="rc-table ex-talent-table">
            <TableExport name="talent-projects" label="this talent's projects" />
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <SortableTh
                    key={column.key}
                    sortKey={column.key}
                    ariaSort={ariaSort}
                    onToggle={toggle}
                    numeric={column.numeric}
                  >
                    {column.label}
                  </SortableTh>
                ))}
                {/* Not a `SortableTh`: the cell holds a link, and ordering by
                    whether one exists would rank projects on whether their client
                    has a numeric Fiverr id, which is not a property of the work. */}
                <th scope="col">Fiverr OS</th>
              </tr>
            </thead>
            <tbody>
              {paged.shown.map((row) => (
                <ProjectRow
                  key={row.id}
                  row={row}
                  open={row.id === selectedId}
                  detailId={DETAIL_ID}
                  onToggle={() =>
                    setSelection({ from: projectId, id: row.id === selectedId ? null : row.id })
                  }
                />
              ))}
            </tbody>
          </table>
          <ShowMoreRow remaining={paged.remaining} onClick={paged.showMore} />
        </div>
      </section>

      {selected ? (
        <SellerThreadDetail
          row={selected}
          id={DETAIL_ID}
          heading={projectTitle({
            id: selected.projectId ?? selected.id,
            name: selected.projectName,
            summary: selected.projectSummary,
          })}
          // The second button the operator asked for: the thread here, the whole
          // project one click away.
          action={
            selected.projectId ? (
              <Link
                to="/explorer/projects/$projectId"
                params={{ projectId: selected.projectId }}
                className="ex-primary-link"
              >
                Open project in Explorer
              </Link>
            ) : null
          }
        />
      ) : null}
    </div>
  );
}

export function ExplorerTalentPage({
  sellerId,
  projectId,
}: {
  sellerId: string;
  projectId?: string;
}) {
  const detail = useQuery(explorerTalentQuery(sellerId));
  const threads = useMemo(() => parseTalentThreads(detail.data?.threads ?? []), [detail.data]);

  if (detail.isPending) {
    return (
      <div className="ad-page">
        <LoadingState label="Loading talent…" />
        <TableSkeleton columns={8} />
      </div>
    );
  }

  // A talent we never approached has no rows at all, which the backend answers as
  // a 404 — that is "no such talent here", not an error to retry.
  if (detail.isError) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return (
        <div className="ad-page">
          <EmptyState
            title="No outreach for this talent"
            message={`We have never approached @${sellerId}, so there is nothing to show.`}
          />
        </div>
      );
    }
    return (
      <div className="ad-page">
        <AreaBoundary area="explorer" error={detail.error} onRetry={() => void detail.refetch()} />
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="ad-page">
        <EmptyState title="No outreach for this talent" />
      </div>
    );
  }

  return <TalentView sellerId={sellerId} threads={threads} projectId={projectId} />;
}
